
import {
    Ref,
    computed,
    defineComponent,
    reactive,
    ref,
    watch,
} from 'vue';
import { useDisabled } from '@redviking/argonaut-core-ui/src/components/EntityDetail/useEntityDetail';
import { ScriptContentUpdatePayload, argoScriptPath, getReferencedAppletVars } from '@redviking/argonaut-util/src/argo-lang/parser';
import { setExtraLibs, useMonacoEditor } from 'src/util/composables/use-monaco-editor';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { getAppletScriptExtraLibs } from '@redviking/argonaut-core-ui/src/applet/design/validations/design.validations';

/**
 * all scripts must share the same model,
 * because monaco's typescript services considers all models
 * part of the same "project", and there's risk for conflicts
 * https://github.com/microsoft/monaco-editor/issues/2976
 */
export const argoScriptUri = monaco.Uri.parse(argoScriptPath);
export const argoScriptModel = monaco.editor.getModel(argoScriptUri) || monaco.editor.createModel('', 'javascript', argoScriptUri);

export default defineComponent({
    name: 'AppletScriptEditor',
    events: [
        'update:scriptContent',
    ],
    props: {
        script: { type: String, required: true },
        varpId: { type: String, required: false, default: null },
        hideDetails: { type: Boolean, required: false, default: false },
        strictReadOnly: { type: Boolean, required: false, default: false },
    },
    setup (props, { emit }) {
        const readOnly = useDisabled.useInject().ref;
        const script = computed({
            get: () => props.script,
            set: newScript => {
                const varsInUse = getReferencedAppletVars(newScript);
                const payload: ScriptContentUpdatePayload = {
                    scriptContent: newScript,
                    varsInUse,
                };
                emit('update:scriptContent', payload);
            },
        });

        const editorRef: Ref<HTMLElement | null> = ref(null);

        useMonacoEditor({
            editorElementRef: editorRef,
            content: script,
            readOnlyRef: readOnly,
            textModel: argoScriptModel,
            strictReadonly: props.strictReadOnly,
        });

        watch(reactive(accessor.appletDesign.allAppletVariables), newAppletVariables => {
            setExtraLibs(getAppletScriptExtraLibs(newAppletVariables));
        }, { immediate: true });

        return {
            editorRef,
        };
    },
});
